import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { renderMarkdown } from "../lib/markdown";
import CaseImageGallery from "../components/CaseImageGallery";
import RequestSpecialistInputModal from "../components/RequestSpecialistInputModal";
import AiOutputDisclaimer from "../components/AiOutputDisclaimer";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  Loader2, Sparkles, ShieldCheck, AlertTriangle, Eye, FileText,
  CheckCircle2, ArrowRight, Lock, UserCog, Users,
} from "lucide-react";

const URGENCY_META = {
  urgent: {
    label: "Urgent",
    dot: "🔴",
    border: "border-red-500/50",
    bg: "bg-red-500/5",
    text: "text-red-400",
  },
  important: {
    label: "Important",
    dot: "🟡",
    border: "border-amber-400/50",
    bg: "bg-amber-400/5",
    text: "text-amber-300",
  },
  monitor: {
    label: "Monitor",
    dot: "🟢",
    border: "border-emerald-500/40",
    bg: "bg-emerald-500/5",
    text: "text-emerald-400",
  },
};

const PATHWAY_LABELS = {
  comprehensive: "Comprehensive Care",
  sequential: "Sequential Care",
  limited: "Limited Care",
};

export default function CaseAnalysisDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [a, setA] = useState(null);
  const [filter, setFilter] = useState("all");
  const [pathway, setPathway] = useState("");
  const [saving, setSaving] = useState(false);
  const [requestingReview, setRequestingReview] = useState(false);
  const [collabModalOpen, setCollabModalOpen] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/case-analysis/${id}`);
      setA(r.data);
      if (r.data.pathway_choice) setPathway(r.data.pathway_choice);
    } catch (_) {
      toast.error("Could not load analysis");
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const acknowledge = async () => {
    if (!pathway) {
      toast.error("Select a treatment pathway to continue");
      return;
    }
    if (
      !window.confirm(
        "Once acknowledged, this analysis becomes a permanent, immutable case-history record. Continue?"
      )
    )
      return;
    setSaving(true);
    try {
      const r = await api.post(`/case-analysis/${id}/acknowledge`, {
        pathway_choice: pathway,
      });
      setA(r.data);
      toast.success("Acknowledged — saved to case history");
      if (pathway === "request_specialist_review") {
        startReviewFlow();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Acknowledgment failed");
    } finally {
      setSaving(false);
    }
  };

  const startReviewFlow = async () => {
    // Tier 3 — Complete Case Review ($250). Uses the tiered review endpoint
    // bound directly to the case analysis (no synthetic consultation needed).
    setRequestingReview(true);
    try {
      const rr = await api.post("/reviews/request-tiered", {
        tier_slug: "complete_case",
        resource_id: a.id,
        origin_url: window.location.origin,
      });
      if (rr.data.checkout_url) {
        window.location.href = rr.data.checkout_url;
      } else {
        toast.success("Specialist review requested");
        // Tier 3 free path: stay on the page; reload to surface status.
        load();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not request specialist review");
    } finally {
      setRequestingReview(false);
    }
  };

  if (!a)
    return (
      <Layout>
        <div className="text-[var(--pa-text-muted)] flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading analysis…
        </div>
      </Layout>
    );

  const findings = a.full_mouth_findings || [];
  const filtered = filter === "all" ? findings : findings.filter((f) => f.urgency === filter);
  const urgentCount = findings.filter((f) => f.urgency === "urgent").length;
  const importantCount = findings.filter((f) => f.urgency === "important").length;
  const monitorCount = findings.filter((f) => f.urgency === "monitor").length;

  return (
    <Layout>
      <div className="flex items-center gap-2 kicker">
        <Sparkles size={12} className="text-[var(--pa-gold)]" /> ProsthoAI Complete Case Analysis
      </div>
      <h1 className="font-serif text-3xl md:text-4xl text-[var(--pa-text)] mt-2" data-testid="cca-detail-title">
        See the whole picture before treating the part
      </h1>

      {/* Presenting concern */}
      <div className="mt-5 p-5 bg-[var(--pa-surface)] border border-[var(--pa-border)] rounded-lg">
        <div className="kicker">Clinician's Presenting Concern</div>
        <p className="mt-1.5 text-[var(--pa-text)] whitespace-pre-wrap">{a.presenting_concern}</p>
        <div className="mt-2 text-xs text-[var(--pa-text-dim)] flex flex-wrap items-center gap-x-2 gap-y-1">
          {a.presenting_module_name && <span>Module: {a.presenting_module_name}</span>}
          {a.presenting_module_name && a.patient_id && <span aria-hidden>·</span>}
          {a.patient_id && (
            <span className="flex items-center gap-1.5">
              Patient:{" "}
              <Link
                to={`/patients/${a.patient_id}`}
                data-testid="cca-detail-patient-link"
                className="text-[var(--pa-gold)] hover:underline underline-offset-2"
              >
                {a.patient_name || "Untitled patient"}
              </Link>
              {(a.patient_name || "").startsWith("Quick Case") && (
                <Link
                  to={`/patients/${a.patient_id}`}
                  data-testid="cca-detail-rename-quickcase"
                  title="Rename this Quick Case into a proper patient record to consolidate future imaging and analyses"
                  className="ml-1 inline-flex items-center gap-1 rounded-full border border-[var(--pa-gold)]/60 bg-[var(--pa-gold)]/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-[var(--pa-gold)] hover:bg-[var(--pa-gold)]/20 transition"
                >
                  <UserCog size={10} /> Rename
                </Link>
              )}
            </span>
          )}
          {(a.presenting_module_name || a.patient_id) && <span aria-hidden>·</span>}
          <span>{new Date(a.created_at).toLocaleString()}</span>
        </div>
      </div>

      <div className="gold-divider my-10" />

      {/* Quick actions */}
      <div className="flex flex-wrap gap-3 mb-6" data-testid="cca-quick-actions">
        <button
          type="button"
          onClick={() => setCollabModalOpen(true)}
          data-testid="cca-request-specialist-input"
          className="flex items-center gap-2 px-4 py-2.5 rounded-md border border-[var(--pa-gold)]/60 bg-[var(--pa-gold)]/5 text-[var(--pa-gold)] hover:bg-[var(--pa-gold)]/10 transition text-sm font-semibold"
        >
          <Users size={14} /> Request Specialist Input
        </button>
        <Link
          to="/collab-cases"
          data-testid="cca-view-collab"
          className="flex items-center gap-2 px-4 py-2.5 rounded-md border border-[var(--pa-border)] text-[var(--pa-text)] hover:border-[var(--pa-gold)]/60 transition text-sm"
        >
          View interdisciplinary cases
        </Link>
      </div>

      <RequestSpecialistInputModal
        analysisId={a.id}
        open={collabModalOpen}
        onClose={() => setCollabModalOpen(false)}
      />

      <AiOutputDisclaimer className="mb-8" />

      {/* STEP 1: Foundational Evaluation (Facial → Occlusal → Study Model → Real Guide) */}
      {/* Image gallery — every file the AI was given to look at */}
      <CaseImageGallery analysisId={a.id} />
      {a.facial_analysis && (
        <section className="mb-10" data-testid="cca-facial">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-[var(--pa-gold)]/10 border border-[var(--pa-gold)] flex items-center justify-center text-[var(--pa-gold)] font-serif">
              1
            </div>
            <div className="flex-1">
              <div className="kicker text-[var(--pa-gold)]">
                Facial Analysis — Beginning of the Evaluation
              </div>
              <h2 className="font-serif text-2xl text-[var(--pa-text)] mt-1">
                Every case begins with facial and occlusal analysis.
              </h2>
            </div>
          </div>
          <div
            className="prose-clinical mt-5 p-6 bg-[var(--pa-surface)] border border-[var(--pa-border-active)] rounded-lg"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(a.facial_analysis) }}
          />
        </section>
      )}

      <section className="mb-10" data-testid="cca-occlusal">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[var(--pa-gold)]/10 border border-[var(--pa-gold)] flex items-center justify-center text-[var(--pa-gold)] font-serif">
            {a.facial_analysis ? "2" : "1"}
          </div>
          <div className="flex-1">
            <div className="kicker text-[var(--pa-gold)]">
              Occlusal Analysis — Foundation of Treatment Planning
            </div>
            <h2 className="font-serif text-2xl text-[var(--pa-text)] mt-1">
              {a.facial_analysis
                ? "Then we address the occlusion."
                : "Before we address your concern, we begin with facial and occlusal analysis."}
            </h2>
          </div>
        </div>
        <div
          className="prose-clinical mt-5 p-6 bg-[var(--pa-surface)] border border-[var(--pa-border-active)] rounded-lg"
          dangerouslySetInnerHTML={{ __html: renderMarkdown(a.occlusal_analysis) }}
        />
      </section>

      {a.study_model_analysis && (
        <section className="mb-10" data-testid="cca-study-model">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-[var(--pa-gold)]/10 border border-[var(--pa-gold)] flex items-center justify-center text-[var(--pa-gold)] font-serif">
              ★
            </div>
            <div className="flex-1">
              <div className="kicker text-[var(--pa-gold)]">
                Mounted Study Model Analysis — Articulator-Based Occlusal Evaluation
              </div>
              <h2 className="font-serif text-2xl text-[var(--pa-text)] mt-1">
                Articulator workup verified.
              </h2>
            </div>
          </div>
          <div
            className="prose-clinical mt-5 p-6 bg-[var(--pa-surface)] border border-[var(--pa-border-active)] rounded-lg"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(a.study_model_analysis) }}
          />
        </section>
      )}

      {a.realguide_verification && (
        <section className="mb-10" data-testid="cca-realguide">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-500/10 border border-emerald-500 flex items-center justify-center text-emerald-400 font-serif">
              ✓
            </div>
            <div className="flex-1">
              <div className="kicker text-emerald-400">
                Restorative-Driven Implant Verification
              </div>
              <h2 className="font-serif text-2xl text-[var(--pa-text)] mt-1">
                Real Guide planning reviewed against the prosthodontic plan.
              </h2>
            </div>
          </div>
          <div
            className="prose-clinical mt-5 p-6 bg-emerald-500/5 border border-emerald-500/40 rounded-lg"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(a.realguide_verification) }}
          />
        </section>
      )}

      {/* STEP 4: Full Mouth Findings */}
      <section className="mb-10" data-testid="cca-findings">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[var(--pa-gold)]/10 border border-[var(--pa-gold)] flex items-center justify-center text-[var(--pa-gold)] font-serif">
            2
          </div>
          <div className="flex-1">
            <div className="kicker text-[var(--pa-gold)]">Full Mouth Findings</div>
            <h2 className="font-serif text-2xl text-[var(--pa-text)] mt-1">
              The complete clinical picture
            </h2>
            <div className="mt-2 flex flex-wrap gap-3 text-xs">
              <span className="text-red-400">🔴 {urgentCount} urgent</span>
              <span className="text-amber-300">🟡 {importantCount} important</span>
              <span className="text-emerald-400">🟢 {monitorCount} monitor</span>
            </div>
          </div>
        </div>

        <div className="mt-5 flex gap-2 flex-wrap">
          {["all", "urgent", "important", "monitor"].map((k) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              data-testid={`cca-filter-${k}`}
              className={`text-xs px-3 py-1.5 rounded-md border transition ${
                filter === k
                  ? "border-[var(--pa-gold)] bg-[var(--pa-gold)]/10 text-[var(--pa-gold)]"
                  : "border-[var(--pa-border)] text-[var(--pa-text-muted)] hover:border-[var(--pa-border-active)]"
              }`}
            >
              {k === "all" ? "All findings" : URGENCY_META[k].label}
            </button>
          ))}
        </div>

        <div className="mt-5 space-y-3">
          {filtered.length === 0 && (
            <div className="text-sm text-[var(--pa-text-muted)] py-6 text-center border border-dashed border-[var(--pa-border)] rounded-lg">
              No findings in this category.
            </div>
          )}
          {filtered.map((f, idx) => {
            const m = URGENCY_META[f.urgency] || URGENCY_META.monitor;
            return (
              <div
                key={idx}
                data-testid={`cca-finding-${f.urgency}-${idx}`}
                className={`border ${m.border} ${m.bg} rounded-lg p-4`}
              >
                <div className="flex items-start gap-3">
                  <div className="text-lg leading-none mt-0.5">{m.dot}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`kicker ${m.text}`}>{m.label}</span>
                      <span className="text-xs text-[var(--pa-text-muted)]">· {f.area}</span>
                      <span className="text-xs text-[var(--pa-text-dim)]">· {f.category}</span>
                      <span className="text-xs text-[var(--pa-text-dim)] ml-auto">
                        Timing: {f.timing}
                      </span>
                    </div>
                    <div className="mt-1.5 text-sm text-[var(--pa-text)]">{f.finding}</div>
                    <div className="mt-1 text-xs text-[var(--pa-text-muted)] italic">
                      Why it matters: {f.implication}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* STEP 5: Three pathways */}
      <section className="mb-10" data-testid="cca-pathways">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[var(--pa-gold)]/10 border border-[var(--pa-gold)] flex items-center justify-center text-[var(--pa-gold)] font-serif">
            3
          </div>
          <div className="flex-1">
            <div className="kicker text-[var(--pa-gold)]">Treatment Pathways</div>
            <h2 className="font-serif text-2xl text-[var(--pa-text)] mt-1">
              Three options to discuss with your patient
            </h2>
          </div>
        </div>

        <div className="mt-6 grid md:grid-cols-3 gap-4">
          {["comprehensive", "sequential", "limited"].map((k) => {
            const p = a.pathways?.[k] || {};
            return (
              <div
                key={k}
                data-testid={`cca-pathway-${k}`}
                className={`bg-[var(--pa-surface)] border ${
                  pathway === k ? "border-[var(--pa-gold)]" : "border-[var(--pa-border)]"
                } rounded-lg p-5`}
              >
                <div className="kicker text-[var(--pa-gold)]">{PATHWAY_LABELS[k]}</div>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="text-xs text-[var(--pa-text-muted)]">Complexity:</span>
                  <span className="text-sm font-semibold text-[var(--pa-text)]">
                    {p.complexity || "—"}
                  </span>
                </div>
                <div className="mt-1.5 flex items-baseline gap-2">
                  <span className="text-xs text-[var(--pa-text-muted)]">Clinical steps:</span>
                  <span className="text-sm text-[var(--pa-text)]">{p.steps_estimate ?? "—"}</span>
                </div>
                <p className="mt-3 text-sm text-[var(--pa-text-muted)] leading-relaxed">
                  {p.summary}
                </p>
                {p.addresses?.length > 0 && (
                  <div className="mt-3">
                    <div className="kicker text-emerald-400">Addresses</div>
                    <ul className="mt-1.5 text-xs text-[var(--pa-text-muted)] space-y-0.5">
                      {p.addresses.map((x, i) => (
                        <li key={i}>· {x}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {p.defers?.length > 0 && (
                  <div className="mt-3">
                    <div className="kicker text-amber-300">Defers</div>
                    <ul className="mt-1.5 text-xs text-[var(--pa-text-muted)] space-y-0.5">
                      {p.defers.map((x, i) => (
                        <li key={i}>· {x}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {p.risks_of_deferral && (
                  <div className="mt-3 text-xs text-red-400/90">
                    <AlertTriangle size={11} className="inline -mt-0.5 mr-1" />
                    Risk: {p.risks_of_deferral}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* STEP 6: Presenting concern answered */}
      <section className="mb-10" data-testid="cca-presenting-response">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[var(--pa-gold)]/10 border border-[var(--pa-gold)] flex items-center justify-center text-[var(--pa-gold)] font-serif">
            4
          </div>
          <div className="flex-1">
            <div className="kicker text-[var(--pa-gold)]">Your Presenting Concern — Answered in Context</div>
            <h2 className="font-serif text-2xl text-[var(--pa-text)] mt-1">
              {a.presenting_module_name
                ? `Now addressing: ${a.presenting_module_name}`
                : "Now addressing your concern"}
            </h2>
          </div>
        </div>
        <div
          className="prose-clinical mt-5 p-6 bg-[var(--pa-surface)] border border-[var(--pa-border-active)] rounded-lg"
          dangerouslySetInnerHTML={{ __html: renderMarkdown(a.presenting_concern_response) }}
        />
      </section>

      {/* STEP 7: Specialist Review Recommendation */}
      {a.specialist_review_recommended && (
        <section
          className="mb-10 p-6 bg-[var(--pa-surface)] border-2 border-[var(--pa-gold)] rounded-lg"
          data-testid="cca-specialist-rec"
        >
          <div className="flex items-start gap-3">
            <ShieldCheck size={24} className="text-[var(--pa-gold)] shrink-0 mt-0.5" />
            <div>
              <div className="kicker text-[var(--pa-gold)]">
                Specialist Review Strongly Recommended
              </div>
              <p className="mt-2 text-sm text-[var(--pa-text)] leading-relaxed">
                This case has findings that warrant specialist-level prosthodontic guidance.
                A review by{" "}
                <strong>Dr. Ronik Seecharan DMD, Prosthodontist</strong> — 30 years of
                clinical practice dedicated to treatment planning excellence — is strongly
                recommended before proceeding with treatment. Dr. Seecharan will review your
                complete case analysis, add clinical guidance, and co-sign the consultation.
              </p>
              {a.specialist_review_reasons?.length > 0 && (
                <ul className="mt-3 text-xs text-[var(--pa-text-muted)] space-y-1">
                  {a.specialist_review_reasons.map((r, i) => (
                    <li key={i}>· {r}</li>
                  ))}
                </ul>
              )}
              <div className="mt-4 text-xs text-[var(--pa-text-dim)]">
                Tier 3 · $250 per complete-case review · Practice/Group plans receive 2–5 free
                reviews per month (up to Tier 2 only)
              </div>
            </div>
          </div>
        </section>
      )}

      {/* STEP 8: Liability acknowledgment */}
      <section
        className="mb-12 p-6 bg-[var(--pa-bg-2)] border border-[var(--pa-gold)]/40 rounded-lg"
        data-testid="cca-ack"
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-[var(--pa-gold)]/10 border border-[var(--pa-gold)] flex items-center justify-center text-[var(--pa-gold)] font-serif">
            5
          </div>
          <div className="flex-1">
            <div className="kicker text-[var(--pa-gold)]">Treatment Acknowledgment</div>
            <h2 className="font-serif text-2xl text-[var(--pa-text)] mt-1">
              Select your treatment approach
            </h2>
            <p className="mt-2 text-xs text-[var(--pa-text-muted)]">
              This selection and the full analysis above will be saved as a permanent,
              immutable record in your case history.
            </p>
          </div>
        </div>

        <div className="space-y-3">
          {[
            {
              v: "comprehensive",
              label: "I will pursue Comprehensive Care as recommended",
            },
            {
              v: "sequential",
              label:
                "I will pursue Sequential or Limited Care — I have reviewed all findings and understand the clinical implications of deferring identified concerns",
              alt_v: "limited",
            },
            {
              v: "request_specialist_review",
              label: "I am requesting a Specialist Review before proceeding",
            },
          ].map((opt) => (
            <label
              key={opt.v}
              data-testid={`cca-pathway-choice-${opt.v}`}
              className={`flex items-start gap-3 p-4 rounded-md border cursor-pointer transition ${
                pathway === opt.v
                  ? "border-[var(--pa-gold)] bg-[var(--pa-gold)]/5"
                  : "border-[var(--pa-border)] hover:border-[var(--pa-border-active)]"
              } ${a.is_finalized ? "opacity-70 cursor-not-allowed" : ""}`}
            >
              <input
                type="radio"
                name="pathway"
                value={opt.v}
                disabled={a.is_finalized}
                checked={pathway === opt.v}
                onChange={(e) => setPathway(e.target.value)}
                className="accent-[var(--pa-gold)] mt-1"
              />
              <span className="text-sm text-[var(--pa-text)] leading-relaxed">{opt.label}</span>
            </label>
          ))}
        </div>

        {!a.is_finalized ? (
          <button
            onClick={acknowledge}
            disabled={saving || !pathway}
            data-testid="cca-acknowledge"
            className="mt-6 flex items-center gap-2 bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold px-6 py-3 rounded-md hover:bg-[var(--pa-gold-hover)] transition disabled:opacity-50"
          >
            {saving ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Saving…
              </>
            ) : (
              <>
                Acknowledge & Save Permanent Record <ArrowRight size={14} />
              </>
            )}
          </button>
        ) : (
          <div className="mt-6 p-4 rounded-md bg-emerald-500/10 border border-emerald-500/30">
            <div className="flex items-start gap-2">
              <CheckCircle2 size={16} className="text-emerald-400 mt-0.5" />
              <div className="text-xs">
                <div className="font-semibold text-emerald-400 flex items-center gap-1.5">
                  Acknowledged & Sealed
                  <Lock size={11} />
                </div>
                <p className="text-[var(--pa-text-muted)] mt-1 leading-relaxed whitespace-pre-wrap">
                  {a.acknowledgment_statement}
                </p>
              </div>
            </div>
            {pathway === "request_specialist_review" && (
              <button
                onClick={startReviewFlow}
                disabled={requestingReview}
                data-testid="cca-start-review"
                className="mt-4 bg-[var(--pa-gold)] text-[var(--pa-bg)] text-sm font-semibold px-4 py-2 rounded-md hover:bg-[var(--pa-gold-hover)] transition"
              >
                {requestingReview ? "Redirecting…" : "Start Specialist Review →"}
              </button>
            )}
          </div>
        )}
      </section>

      <div className="text-xs text-[var(--pa-text-dim)] text-center pt-6 border-t border-[var(--pa-border)]">
        Patent Pending · US Application 64/062,147 · Dr. Ronik Seecharan DMD, Prosthodontist —
        30 years of clinical practice dedicated to treatment planning excellence
      </div>
    </Layout>
  );
}
